import React, { useEffect, useState } from 'react';
import { useGlobal } from '../../global/GlobalContext';
import axios from 'axios';
import Skeleton from 'react-loading-skeleton';
import { toast } from 'react-toastify';
import { FiUser, FiEdit2 } from 'react-icons/fi';

const Field = ({ label, name, value, onChange, disabled, type = 'text', placeholder }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1.5">{label}</label>
    <input
      type={type}
      name={name}
      value={value ?? ''}
      onChange={onChange}
      disabled={disabled}
      placeholder={placeholder || (disabled ? 'Not provided' : '')}
      className={`w-full px-3 py-2.5 border rounded-xl text-sm focus:outline-none ${
        disabled
          ? 'bg-gray-50 border-gray-100 text-gray-500'
          : 'border-gray-200 focus:ring-2 focus:ring-brand-600/20 focus:border-brand-600'
      }`}
    />
  </div>
);

const MyProfile = () => {
  const { token } = useGlobal();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [updatedProfile, setUpdatedProfile] = useState({});

  useEffect(() => {
    const fetchProfile = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/v1/get_customer_details`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.data.status) {
          setProfile(response.data.customer_data);
          setUpdatedProfile(response.data.customer_data);
        } else {
          setLoadError(true);
        }
      } catch (error) {
        setLoadError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [token]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setUpdatedProfile({ ...updatedProfile, [name]: value });
  };

  const handleUpdate = async () => {
    setSaving(true);
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/api/v1/update_customer_profile`, updatedProfile, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      setProfile(updatedProfile);
      setIsEditing(false);
      toast.success('Profile updated successfully');
    } catch (error) {
      toast.error('Failed to update profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setUpdatedProfile(profile);
    setIsEditing(false);
  };

  return (
    <div className="max-w-3xl mx-auto">
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 md:p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-full bg-brand-600 text-white flex items-center justify-center font-bold uppercase shrink-0">
              {profile?.first_name?.charAt(0) || <FiUser size={18} />}
            </div>
            <div>
              <h2 className="font-semibold text-gray-900">My Profile</h2>
              <p className="text-xs text-gray-500">Your personal information</p>
            </div>
          </div>

          {!loading && !loadError && !isEditing && (
            <button
              onClick={() => setIsEditing(true)}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-600 bg-brand-600/10 px-3.5 py-2 rounded-xl"
            >
              <FiEdit2 size={14} /> Edit
            </button>
          )}
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[...Array(8)].map((_, i) => (
              <Skeleton key={i} height={44} className="rounded-xl" />
            ))}
          </div>
        ) : loadError ? (
          <div className="text-center py-10">
            <p className="text-sm text-gray-500">Couldn't load your profile. Please refresh and try again.</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field
                label="Full Name"
                name="first_name"
                value={updatedProfile.first_name}
                onChange={handleChange}
                disabled={!isEditing}
              />
              <Field label="Gender" value={profile.gender} disabled />
              <Field label="Date of Birth" value={profile.dob} disabled />
              <Field
                label="Email"
                name="email"
                type="email"
                value={updatedProfile.email}
                onChange={handleChange}
                disabled={!isEditing}
              />
              <Field
                label="Mobile Number"
                name="mobile_number"
                value={updatedProfile.mobile_number}
                onChange={handleChange}
                disabled={!isEditing}
              />
              <Field
                label="Alternate Mobile Number"
                name="alt_mob_number"
                value={updatedProfile.alt_mob_number}
                onChange={handleChange}
                disabled={!isEditing}
              />
              <Field
                label="Address Line 1"
                name="address_line_1"
                value={updatedProfile.address_line_1}
                onChange={handleChange}
                disabled={!isEditing}
              />
              <Field
                label="City"
                name="city"
                value={updatedProfile.city}
                onChange={handleChange}
                disabled={!isEditing}
              />
              <Field
                label="State"
                name="state"
                value={updatedProfile.state}
                onChange={handleChange}
                disabled={!isEditing}
              />
              <Field
                label="Zip Code"
                name="zip_code"
                value={updatedProfile.zip_code}
                onChange={handleChange}
                disabled={!isEditing}
              />
              <Field
                label="Country"
                name="country"
                value={updatedProfile.country}
                onChange={handleChange}
                disabled={!isEditing}
              />
            </div>

            {isEditing && (
              <div className="flex justify-end gap-3 mt-6 pt-5 border-t border-gray-100">
                <button
                  onClick={handleCancel}
                  className="px-4 py-2.5 bg-gray-100 text-gray-700 rounded-xl text-sm font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdate}
                  disabled={saving}
                  className="px-5 py-2.5 bg-brand-600 text-white rounded-xl text-sm font-medium disabled:opacity-60"
                >
                  {saving ? (
                    <span className="inline-flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                      Saving...
                    </span>
                  ) : (
                    'Save Changes'
                  )}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default MyProfile;
